// Utility for managing practice mode timer across multiple sections

/**
 * Calculate combined timer start and end times for practice mode
 * @param {number} timerMinutes - Timer duration in minutes
 * @returns {Object} - { startTime: ISO string, endTime: ISO string }
 */
export const createPracticeTimer = (timerMinutes) => {
  const now = new Date();
  const endTime = new Date(now.getTime() + timerMinutes * 60 * 1000);

  return {
    startTime: now.toISOString(),
    endTime: endTime.toISOString(),
  };
};

/**
 * Get remaining time in seconds from an end time
 * @param {string} endTime - ISO string of end time
 * @returns {number} - Remaining seconds (0 if expired)
 */
export const getRemainingTime = (endTime) => {
  if (!endTime) return 0;

  const now = new Date();
  const endDateTime = new Date(endTime);
  const remainingMs = endDateTime.getTime() - now.getTime();

  return Math.max(0, Math.floor(remainingMs / 1000));
};

/**
 * Format seconds into MM:SS format
 * @param {number} seconds - Total seconds
 * @returns {string} - Formatted time string
 */
export const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(
    remainingSeconds
  ).padStart(2, "0")}`;
};

/**
 * Check if timer has expired
 * @param {string} endTime - ISO string of end time
 * @returns {boolean} - True if timer expired
 */
export const isTimerExpired = (endTime) => {
  return getRemainingTime(endTime) <= 0;
};

/**
 * Clear practice timer data from sessionStorage
 * Should be called when exiting practice mode or completing test
 */
export const clearPracticeTimer = () => {
  try {
    sessionStorage.removeItem("practiceTimerData");
    console.log("Practice timer data cleared from sessionStorage");
  } catch (error) {
    console.warn("Failed to clear practice timer data:", error);
  }
};

/**
 * Store timer data in sessionStorage
 * @param {string} startTime - ISO string of start time
 * @param {string} endTime - ISO string of end time
 * @param {string} testAttemptId - Unique identifier for this test attempt
 */
export const storeTimerData = (startTime, endTime, testAttemptId = null) => {
  try {
    const timerData = {
      startTime,
      endTime,
      testAttemptId,
      timestamp: Date.now(),
    };
    sessionStorage.setItem("practiceTimerData", JSON.stringify(timerData));
    console.log("Timer data stored in sessionStorage:", timerData);
  } catch (error) {
    console.warn("Failed to store timer data:", error);
  }
};

/**
 * Get stored timer data from sessionStorage
 * @param {string} currentTestAttemptId - Current test attempt ID to validate timer
 * @returns {Object|null} - Timer data or null if not found/invalid
 */
export const getStoredTimerData = (currentTestAttemptId = null) => {
  try {
    const stored = sessionStorage.getItem("practiceTimerData");
    if (!stored) return null;

    const timerData = JSON.parse(stored);

    // If we have a current test attempt ID, check if timer belongs to this attempt
    if (
      currentTestAttemptId &&
      timerData.testAttemptId &&
      timerData.testAttemptId !== currentTestAttemptId
    ) {
      console.log("Timer data is for different test attempt, clearing...");
      clearPracticeTimer();
      return null;
    }

    // Check if timer is not too old (max 24 hours)
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    if (Date.now() - timerData.timestamp > maxAge) {
      console.log("Timer data is too old, clearing...");
      clearPracticeTimer();
      return null;
    }

    return timerData;
  } catch (error) {
    console.warn("Failed to get stored timer data:", error);
    return null;
  }
};
